import { useEffect } from "react";

/**
 * Keep the layout inside the part of the screen the keyboard has not taken.
 *
 * ## Why `100vh` was not enough
 *
 * The shell was sized with `height: 100vh`, which is the height of the *screen*.
 * It ignores the browser's own chrome and it ignores the on-screen keyboard
 * completely, so an agent answering from a phone had the reply box sitting
 * underneath the keyboard. With the emoji keyboard — taller than the letter one —
 * the box disappeared altogether, which reads as "emoji don't work here" rather
 * than as a layout bug.
 *
 * `100dvh` fixes the chrome half and does nothing for the keyboard, by design.
 * `window.visualViewport` is the one measurement that shrinks when a keyboard
 * opens, so it is read here and published as a CSS variable — the layout stays in
 * CSS, and this stays a measurement.
 *
 * No state and no re-render: writing a variable avoids re-rendering the whole
 * dashboard on every frame of the keyboard's opening animation.
 */

const HEIGHT_VARIABLE = "--island-viewport-height";

export function useVisualViewport(): void {
  useEffect(() => {
    const viewport = window.visualViewport;

    if (viewport === null) return;

    const root = document.documentElement;

    const apply = (): void => {
      root.style.setProperty(HEIGHT_VARIABLE, `${String(viewport.height)}px`);
    };

    apply();

    viewport.addEventListener("resize", apply);

    return () => {
      viewport.removeEventListener("resize", apply);
      root.style.removeProperty(HEIGHT_VARIABLE);
    };
  }, []);
}
